/**
 * @fileoverview List filter state ↔ the column's filter groups
 *
 * A row passes when its value is checked AND it matches the text conditions:
 * - "only" selection: `{OR, [EQ id…, BLANK]}` (without Blank, the shape saved before conditions existed)
 * - "all except" selection: `{AND, [NOT_BLANK, NEQ id…]}`
 * - paired option (with `pairColKey`): `{AND, [EQ term, EQ pairTerm on pairColKey]}` in place of its `EQ id`, and
 *   `{OR, [NEQ term, NEQ pairTerm on pairColKey]}` in place of its `NEQ id`
 * - conditions only: `{AND, [chain]}`, wrapped so a chain of Equals rows never reads as a selection
 * - both: `{AND, [selection, chain]}`
 * - combined column: the selection on `colKey` and the chain on `searchColKey`, as two groups
 */

import type { ConditionRow } from "../condition/conditionChain";
import { buildConditionGroup, completeConditionRows, parseConditionGroup } from "../condition/conditionChain";
import { LIST_CONDITION_DEFAULT_OPERATOR } from "../condition/operators";
import type { ColFilter, ColFilterGroup, ColFilterNode } from "../filterModel";
import { FilterOperator, isColFilterGroup, LogicalOperator } from "../filterModel";
import { pairedListOptionId, parsePairedListOptionId } from "./pairedListOption";

export interface ListSelection {
  /** "exclude": every option is checked except `ids`; "include": only `ids` are checked. */
  mode: "include" | "exclude";
  ids: string[];
  /** Whether "(Blank)" is checked; ignored on a column without the Blank row. */
  blank: boolean;
}

export interface ListFilterState {
  /** `null`: every option is checked. */
  selection: ListSelection | null;
  /** Complete rows only. */
  conditionRows: ConditionRow[];
}

export interface ListFilterKeys {
  searchColKey?: string;
  pairColKey?: string;
  /** The column has the "(Blank)" row; without it Blank / Not blank leaves are text conditions. */
  blankOption?: boolean;
}

export interface BuildListFilterOptions extends ListFilterState, ListFilterKeys {
  colKey: string;
}

export interface ListSelectionFlags {
  blankOption?: boolean;
  multiValue?: boolean;
}

interface PairedTerms {
  term: string;
  pairTerm: string;
}

/**
 * The checked items of `universe` (the options plus the orphan ids) and "(Blank)" as the shorter form: "all except"
 * when no more items are unchecked than checked, else "only". A `multiValue` column always uses "only", except for
 * "all but (Blank)". `null`: everything is checked; `"none"`: nothing is. Checked ids outside `universe` are dropped.
 */
export function encodeSelection(
  checkedIds: readonly string[],
  blankChecked: boolean,
  universe: readonly string[],
  { blankOption = false, multiValue = false }: ListSelectionFlags = {},
): ListSelection | null | "none" {
  const checked = new Set(checkedIds);
  const included = universe.filter((id) => checked.has(id));
  const excluded = universe.filter((id) => !checked.has(id));
  const blank = blankOption && blankChecked;
  const checkedCount = included.length + (blank ? 1 : 0);
  const uncheckedCount = excluded.length + (blankOption && !blank ? 1 : 0);

  if (uncheckedCount === 0) return null;
  if (checkedCount === 0) return "none";
  if (excluded.length === 0 || (!multiValue && uncheckedCount <= checkedCount)) {
    return { mode: "exclude", ids: excluded, blank };
  }
  return { mode: "include", ids: included, blank };
}

export function buildListFilterGroups(options: BuildListFilterOptions): ColFilterGroup[] | null {
  const { colKey, selection, conditionRows, searchColKey } = options;
  const selectionGroup = selection ? buildSelectionGroup(colKey, selection, options) : null;

  if (searchColKey) {
    const groups = [selectionGroup, buildConditionGroup(searchColKey, conditionRows)].filter(
      (group): group is ColFilterGroup => group !== null,
    );
    return groups.length > 0 ? groups : null;
  }

  const chain = buildConditionGroup(colKey, conditionRows);
  if (!chain) return selectionGroup ? [selectionGroup] : null;
  return [
    { colKey, logicalOperator: LogicalOperator.AND, conditions: selectionGroup ? [selectionGroup, chain] : [chain] },
  ];
}

function buildSelectionGroup(colKey: string, selection: ListSelection, keys: ListFilterKeys): ColFilterGroup | null {
  const exclude = selection.mode === "exclude";
  const items = selection.ids.map((id) => selectionItem(colKey, id, keys.pairColKey, exclude));
  if (keys.blankOption && selection.blank !== exclude) {
    const blankLeaf = leaf(colKey, exclude ? FilterOperator.NOT_BLANK : FilterOperator.BLANK, "");
    if (exclude) items.unshift(blankLeaf);
    else items.push(blankLeaf);
  }
  if (items.length === 0) return null;
  return { colKey, logicalOperator: exclude ? LogicalOperator.AND : LogicalOperator.OR, conditions: items };
}

function selectionItem(colKey: string, id: string, pairColKey: string | undefined, exclude: boolean): ColFilterNode {
  const operator = exclude ? FilterOperator.NOT_EQUALS : FilterOperator.EQUALS;
  const pair = pairColKey ? parsePairedListOptionId(id) : null;
  if (!pairColKey || !pair) return leaf(colKey, operator, id);
  return {
    colKey,
    logicalOperator: exclude ? LogicalOperator.OR : LogicalOperator.AND,
    conditions: [leaf(colKey, operator, pair.term), leaf(pairColKey, operator, pair.pairTerm)],
  };
}

function leaf(colKey: string, operator: FilterOperator, filterTerm: string): ColFilter {
  return { colKey, filterTerm, operator };
}

/**
 * The inverse of {@link buildListFilterGroups}, plus the flat groups saved before conditions
 * existed: their Equals leaves are the selection, every other leaf a condition row joined by the
 * group's operator. A flat "Does not equal" group reads as "all except", which keeps its rows. A
 * combined filter's groups come in no guaranteed order, hence the lookup by colKey. On a combined
 * column only the `searchColKey` group holds conditions. Paired options are read only with
 * `pairColKey`.
 */
export function parseListFilterGroups(
  model: ColFilterGroup[] | null | undefined,
  colKey: string,
  keys: ListFilterKeys = {},
): ListFilterState {
  const { searchColKey } = keys;
  const listGroup = model?.find((entry) => entry.colKey === colKey);
  const { selection, conditionRows } = parseListGroup(listGroup, colKey, keys);
  if (!searchColKey) return { selection, conditionRows };

  const searchGroup = model?.find((entry) => entry.colKey === searchColKey);
  return { selection, conditionRows: parseRows(searchGroup) };
}

function parseListGroup(group: ColFilterGroup | undefined, colKey: string, keys: ListFilterKeys): ListFilterState {
  if (!group || group.conditions.length === 0) return { selection: null, conditionRows: [] };

  const selection = parseSelection(group, colKey, keys);
  if (selection !== undefined) return { selection, conditionRows: [] };

  const [first, second] = group.conditions;
  if (!group.conditions.some(isColFilterGroup)) {
    const leaves = group.conditions as ColFilter[];
    const ids = leaves
      .filter(isEqualsLeaf)
      .map((item) => item.filterTerm)
      .filter(Boolean);
    return {
      selection: ids.length > 0 ? { mode: "include", ids, blank: false } : null,
      conditionRows: parseRows({ ...group, conditions: leaves.filter((item) => !isEqualsLeaf(item)) }),
    };
  }
  if (group.logicalOperator === LogicalOperator.AND && group.conditions.length === 1 && isColFilterGroup(first)) {
    return { selection: null, conditionRows: parseRows(first) };
  }
  if (group.logicalOperator === LogicalOperator.AND && group.conditions.length === 2 && isColFilterGroup(second)) {
    const firstSelection = parseSelection(first, colKey, keys);
    if (firstSelection !== undefined) return { selection: firstSelection, conditionRows: parseRows(second) };
  }
  return { selection: null, conditionRows: parseRows(group) };
}

/** `undefined` when `node` is not a selection; `null` when it is one that checks everything. */
function parseSelection(node: ColFilterNode, colKey: string, keys: ListFilterKeys): ListSelection | null | undefined {
  if (!isColFilterGroup(node) || node.conditions.length === 0) return undefined;
  const single = node.conditions.length === 1;
  const include =
    single || node.logicalOperator === LogicalOperator.OR ? readSelection(node, "include", colKey, keys) : undefined;
  if (include !== undefined) return include;
  return single || node.logicalOperator === LogicalOperator.AND
    ? readSelection(node, "exclude", colKey, keys)
    : undefined;
}

function readSelection(
  group: ColFilterGroup,
  mode: ListSelection["mode"],
  colKey: string,
  { pairColKey, blankOption }: ListFilterKeys,
): ListSelection | null | undefined {
  const exclude = mode === "exclude";
  const isIdLeaf = exclude ? isNotEqualsLeaf : isEqualsLeaf;
  const blankOperator = exclude ? FilterOperator.NOT_BLANK : FilterOperator.BLANK;
  const ids: string[] = [];
  let blankLeaves = 0;

  for (const item of group.conditions) {
    if (blankOption && isLeafWith(item, blankOperator)) {
      blankLeaves += 1;
    } else if (isIdLeaf(item)) {
      if (item.filterTerm) ids.push(item.filterTerm);
    } else {
      const pair = pairedTerms(item, colKey, pairColKey, exclude);
      if (!pair) return undefined;
      ids.push(pairedListOptionId(pair.term, pair.pairTerm));
    }
  }
  if (blankLeaves > 1) return undefined;

  const blank = exclude ? blankLeaves === 0 : blankLeaves === 1;
  const checksEverything = exclude ? ids.length === 0 && blank : ids.length === 0 && !blank;
  return checksEverything ? null : { mode, ids, blank };
}

function isLeafWith(node: ColFilterNode, operator: FilterOperator): node is ColFilter {
  return !isColFilterGroup(node) && node.operator === (operator as string);
}

function isEqualsLeaf(node: ColFilterNode): node is ColFilter {
  return !isColFilterGroup(node) && (!node.operator || node.operator === (FilterOperator.EQUALS as string));
}

function isNotEqualsLeaf(node: ColFilterNode): node is ColFilter {
  return isLeafWith(node, FilterOperator.NOT_EQUALS);
}

/**
 * A paired option: one Equals on the column and one on `pairColKey` joined by AND, or for "all except" one Does not
 * equal on each joined by OR; in either order.
 */
function pairedTerms(
  node: ColFilterNode,
  colKey: string,
  pairColKey: string | undefined,
  exclude: boolean,
): PairedTerms | null {
  const joinedBy = exclude ? LogicalOperator.OR : LogicalOperator.AND;
  if (!pairColKey || !isColFilterGroup(node) || node.logicalOperator !== joinedBy) return null;
  const isTermLeaf = exclude ? isNotEqualsLeaf : isEqualsLeaf;
  if (node.conditions.length !== 2 || !node.conditions.every(isTermLeaf)) return null;
  const leaves = node.conditions;
  const termLeaf = leaves.find((item) => item.colKey === colKey);
  const pairLeaf = leaves.find((item) => item.colKey === pairColKey);
  if (!termLeaf?.filterTerm || !pairLeaf?.filterTerm) return null;
  return { term: termLeaf.filterTerm, pairTerm: pairLeaf.filterTerm };
}

function parseRows(group: ColFilterGroup | undefined): ConditionRow[] {
  return completeConditionRows(
    parseConditionGroup(group, { kind: "text", defaultOperator: LIST_CONDITION_DEFAULT_OPERATOR }),
  );
}
